import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

// Utils
import { FormattedMessage, useIntl } from '../../../util/reactIntl';

// Shared components
import { Modal } from '../../../components';

import css from './ShareListing.module.css';

const COPIED_MESSAGE_TIMEOUT = 2000;

const IconShare = () => (
  <svg className={css.shareIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 3v12M12 3l-4 4M12 3l4 4M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Simple brand glyphs (24x24 viewBox) so we don't need an extra dependency.
const ICON_PATHS = {
  whatsapp:
    'M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.3-.5.1-1 .1-1.7-.1-.4-.1-.9-.3-1.5-.6-2.7-1.2-4.4-3.9-4.5-4.1-.1-.2-1.1-1.4-1.1-2.7s.7-1.9.9-2.2c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.3 0 .5l-.3.5-.4.4c-.1.1-.3.3-.1.6.2.3.7 1.1 1.4 1.8 1 .9 1.8 1.1 2.1 1.3.3.1.4.1.6-.1l.8-1c.2-.3.4-.2.6-.1l1.9.9c.3.1.5.2.5.3.1.1.1.7-.1 1.3z',
  facebook:
    'M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z',
  x:
    'M17.8 3h3l-6.6 7.6L22 21h-6.1l-4.8-6.3L5.6 21h-3l7.1-8.1L2.2 3h6.2l4.3 5.7L17.8 3zm-1 16.2h1.7L7.3 4.7H5.5l11.3 14.5z',
  telegram:
    'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm4.6 6.8-1.6 7.7c-.1.6-.5.7-1 .4l-2.7-2-1.3 1.3c-.2.2-.3.3-.6.3l.2-2.8 5-4.5c.2-.2 0-.3-.3-.1l-6.2 3.9-2.7-.8c-.6-.2-.6-.6.1-.9l10.4-4c.5-.2.9.1.7 1.5z',
  linkedin:
    'M20.4 2H3.6C2.7 2 2 2.7 2 3.6v16.8c0 .9.7 1.6 1.6 1.6h16.8c.9 0 1.6-.7 1.6-1.6V3.6c0-.9-.7-1.6-1.6-1.6zM8 19H5V9.5h3V19zM6.5 8.2a1.7 1.7 0 1 1 0-3.5 1.7 1.7 0 0 1 0 3.5zM19 19h-3v-4.6c0-1.1 0-2.5-1.5-2.5S12.8 13 12.8 14.3V19h-3V9.5h2.8v1.3h.1c.4-.8 1.4-1.6 2.9-1.6 3.1 0 3.6 2 3.6 4.6V19z',
  pinterest:
    'M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.2-5s-.3-.6-.3-1.5c0-1.4.8-2.5 1.8-2.5.9 0 1.3.7 1.3 1.4 0 .9-.6 2.2-.9 3.4-.2 1 .5 1.9 1.6 1.9 1.9 0 3.3-2 3.3-4.9 0-2.5-1.8-4.3-4.4-4.3-3 0-4.8 2.3-4.8 4.6 0 .9.4 1.9.8 2.4.1.1.1.2.1.3l-.3 1.2c0 .2-.2.2-.4.1-1.4-.6-2.2-2.6-2.2-4.2 0-3.4 2.5-6.6 7.2-6.6 3.8 0 6.7 2.7 6.7 6.3 0 3.7-2.4 6.8-5.6 6.8-1.1 0-2.2-.6-2.5-1.3l-.7 2.6c-.2.9-.9 2.1-1.3 2.8A10 10 0 1 0 12 2z',
  email:
    'M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm1 2.4V17h16V7.4l-8 5.3-8-5.3zM5.2 7 12 11.5 18.8 7H5.2z',
  sms:
    'M4 3h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 1-2zm3 6.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm5 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm5 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z',
};

const NetworkIcon = ({ name }) => (
  <svg className={css.networkIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path d={ICON_PATHS[name]} fill="currentColor" fillRule="evenodd" />
  </svg>
);

const buildShareLinks = ({ url, title, text, imageUrl }) => {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  const message = encodeURIComponent(`${text}\n${url}`);
  return [
    { key: 'whatsapp', href: `https://wa.me/?text=${message}` },
    { key: 'facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { key: 'x', href: `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
    { key: 'telegram', href: `https://t.me/share/url?url=${u}&text=${t}` },
    { key: 'linkedin', href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    {
      key: 'pinterest',
      href: `https://pinterest.com/pin/create/button/?url=${u}&description=${t}${
        imageUrl ? `&media=${encodeURIComponent(imageUrl)}` : ''
      }`,
    },
    { key: 'email', href: `mailto:?subject=${t}&body=${message}`, sameWindow: true },
    { key: 'sms', href: `sms:?&body=${message}`, sameWindow: true },
  ];
};

// Fallback for older browsers / insecure contexts / denied clipboard permission
const copyWithExecCommand = text => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!ok) {
    throw new Error('Copy failed');
  }
};

const copyToClipboard = text => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(() => copyWithExecCommand(text));
  }
  return new Promise(resolve => resolve(copyWithExecCommand(text)));
};

/**
 * Share button that opens a modal with ways to share a listing (social networks, email, SMS,
 * native share sheet when available) and a copy-link field.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string} props.url absolute URL of the listing
 * @param {string} props.title listing title
 * @param {string?} props.imageUrl listing image URL (used by Pinterest)
 * @param {Function} props.onManageDisableScrolling
 * @returns {JSX.Element}
 */
const ShareListing = props => {
  const { className, url, title, imageUrl, onManageDisableScrolling } = props;
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setCanNativeShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const shareText = intl.formatMessage({ id: 'ShareListing.shareText' }, { title });
  const links = buildShareLinks({ url, title, text: shareText, imageUrl });

  const handleCopy = () => {
    copyToClipboard(url)
      .then(() => {
        setCopied(true);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), COPIED_MESSAGE_TIMEOUT);
      })
      .catch(() => setCopied(false));
  };

  const handleNativeShare = () => {
    navigator.share({ title, text: shareText, url }).catch(() => {
      // User cancelled or share failed – nothing to do.
    });
  };

  const handleOpen = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className={classNames(css.shareButton, className)}
        onClick={handleOpen}
        aria-label={intl.formatMessage({ id: 'ShareListing.openButton' })}
        title={intl.formatMessage({ id: 'ShareListing.openButton' })}
      >
        <IconShare />
      </button>
      <Modal
        id="ShareListingModal"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        contentClassName={css.modalContent}
      >
        <h2 className={css.modalTitle}>
          <FormattedMessage id="ShareListing.modalTitle" />
        </h2>
        <p className={css.listingTitle}>{title}</p>

        <ul className={css.networks}>
          {links.map(({ key, href, sameWindow }) => (
            <li key={key}>
              <a
                className={classNames(css.network, css[key])}
                href={href}
                {...(sameWindow ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
              >
                <span className={css.networkIconWrapper}>
                  <NetworkIcon name={key} />
                </span>
                <span className={css.networkLabel}>
                  <FormattedMessage id={`ShareListing.network.${key}`} />
                </span>
              </a>
            </li>
          ))}
          {canNativeShare ? (
            <li>
              <button
                type="button"
                className={classNames(css.network, css.more)}
                onClick={handleNativeShare}
              >
                <span className={css.networkIconWrapper}>
                  <IconShare />
                </span>
                <span className={css.networkLabel}>
                  <FormattedMessage id="ShareListing.network.more" />
                </span>
              </button>
            </li>
          ) : null}
        </ul>

        <label className={css.copyLabel} htmlFor="ShareListing.url">
          <FormattedMessage id="ShareListing.copyLabel" />
        </label>
        <div className={css.copyRow}>
          <input
            id="ShareListing.url"
            className={css.copyInput}
            type="text"
            value={url}
            readOnly
            onFocus={e => e.target.select()}
          />
          <button type="button" className={css.copyButton} onClick={handleCopy}>
            {copied ? (
              <FormattedMessage id="ShareListing.copied" />
            ) : (
              <FormattedMessage id="ShareListing.copy" />
            )}
          </button>
        </div>
        <p className={css.copyStatus} role="status" aria-live="polite">
          {copied ? <FormattedMessage id="ShareListing.copiedToClipboard" /> : null}
        </p>
      </Modal>
    </>
  );
};

export default ShareListing;
